/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { IInspectorControl_RouteSelector, TInspectorControlProps } from "../InspectorControlTypes";
import {
	FieldGroup,
	FieldResourceSelector,
	FieldResourceSelectorPanel,
	FieldWrapper,
	Icon,
	InputAdornment,
	InputButton
} from "@adapptio/lib-editor-ui";
import { useBlueprintModel } from "../../../ui/hooks/useBlueprintModel";
import { useEditorInspectorContext } from "../../../ui/contexts/EditorInspectorContext";
import { EditorMainContext } from "../../../ui/contexts/EditorMainContext";
import { useTranslate } from "@adapptio/lib-components";
import { termsEditorCommon } from "../../../terms/common";
import { useEscKey } from "../../../ui/hooks/useEscKey";
import { isNilOrEmptyString, offEvent, onEvent } from "@adapptio/lib-shared";
import { RouteList } from "../../../ui/components/RouteList";
import { useCommand } from "../../../ui/hooks";

export interface IInspectorControlRouteSelectorProps
	extends TInspectorControlProps<IInspectorControl_RouteSelector> {}

export const InspectorControlRouteSelector: React.FC<IInspectorControlRouteSelectorProps> = ({
	model: _model
}) => {
	const editorCtx = useContext(EditorMainContext);
	const inspectorContext = useEditorInspectorContext();

	const { model, validationErrors } = useBlueprintModel(_model, "constString");

	const [ openRoutesEditor ] = useCommand("routesEditor.open");

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	const handleClear = useCallback(() => {
		model.schema.setValue(model, null, true);
	}, [ model ]);

	const value = model.value;
	const placeholder = undefined;
	const onChange = handleChange;
	const listAnchor = undefined;
	const listDirection = undefined;
	const listOffset = undefined;
	const readOnly = inspectorContext?.readOnly;

	// @TODO: Move this to adpt-editor-ui FieldResource
	const t = useTranslate();

	const [ isListOpened, setIsListOpened ] = useState(false);
	const selectorRef = useRef<HTMLButtonElement>();

	const [ label, setLabel ] = useState(() => {
		const route = value ? editorCtx.resourceService.getRouteByName(value) : null;
		return value
			? route?.label || t("editor", termsEditorCommon.fields.routeSelector.notFound)
			: t("editor", placeholder) || t("editor", termsEditorCommon.fields.routeSelector.select);
	});

	const toggleRouteList = useCallback(() => {
		setIsListOpened((x) => !x);
	}, []);

	const closeRouteList = useCallback(() => {
		setIsListOpened(false);
	}, []);

	const handleSelect = useCallback(
		(routeKey: string) => {
			onChange(routeKey);
			setIsListOpened(false);
		},
		[ onChange ]
	);

	const handleCreate = useCallback(() => {
		openRoutesEditor().catch((err) => {
			console.error("Can't open Routes Editor:", err);
		});

		closeRouteList();
	}, [ openRoutesEditor, closeRouteList ]);

	useEscKey(() => setIsListOpened(false), isListOpened);

	useEffect(() => {
		if (value) {
			const handleChange = () => {
				const route = editorCtx.resourceService.getRouteByName(value);
				setLabel(route ? route.label : t("editor", termsEditorCommon.fields.routeSelector.notFound));
			};

			handleChange();

			onEvent(editorCtx.resourceService.onRouteListChange, handleChange);

			return () => {
				offEvent(editorCtx.resourceService.onRouteListChange, handleChange);
			};
		} else {
			setLabel(t("editor", placeholder) || t("editor", termsEditorCommon.fields.routeSelector.select));
		}
	}, [ editorCtx.resourceService, value ]);

	const resourceSelected = !isNilOrEmptyString(value);
	const showClear = !model.schema.opts.constraints?.required && resourceSelected;

	return (
		<FieldGroup grow>
			<FieldWrapper type="resource" grow isFocused>
				<FieldResourceSelector
					ref={selectorRef}
					onClick={toggleRouteList}
					disabled={readOnly}
					tooltip={label}
					errors={validationErrors}
					endAdornment={
						<InputAdornment>
							{showClear && (
								<InputButton
									type="button"
									title="Clear"
									onClick={handleClear}
									disabled={!resourceSelected}
								>
									<Icon name="mdi/close" />
								</InputButton>
							)}
							<InputButton type="button" onClick={handleCreate} title="Create new route">
								<Icon name="mdi/plus" />
							</InputButton>
						</InputAdornment>
					}
				>
					{label}
				</FieldResourceSelector>

				<FieldResourceSelectorPanel
					anchorEl={selectorRef}
					isOpened={isListOpened}
					panelAnchor={listAnchor}
					panelDirection={listDirection}
					panelOffset={listOffset}
					onClose={closeRouteList}
					title="Select route"
					onCreate={handleCreate}
				>
					<RouteList
						resourceService={editorCtx.resourceService}
						selectedRouteKey={value}
						onSelect={handleSelect}
						showCreateBtn
						onCreate={handleCreate}
					/>
				</FieldResourceSelectorPanel>
			</FieldWrapper>
		</FieldGroup>
	);
};
